import type { EProjectType, IActionDefinitionDto } from "@kuika/kuika-cl-model";
import type { CascaderOptionType } from "antd/es/cascader";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { highlightCascaderKeyword } from "../../../../../shared/utility/cascader-search";
import { MiddleTruncate } from "../../../../../shared/utility/middle-truncate";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs, { type KuikaTabOption } from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { PICKER_COLLISION_PADDING, PICKER_SIDE_OFFSET } from "../picker-columns/picker-placement";
import {
  keepPickerFieldFocus,
  keepPickerFocusInField,
  stopPickerKeyPropagation,
  useCloseOnIframeFocus,
  usePickerColumns,
  useRevealNewestColumn,
  type IPickerColumn,
  type IPickerColumnsController
} from "../picker-columns/use-picker-columns";
import {
  buildSearchIndex,
  EActionPickerNodeKind,
  getCategoryAncestor,
  getNodeChain,
  getSelectedOptionsPath,
  isAddableNode,
  isBranchNode,
  searchActionPicker,
  toActionPickerNodes,
  toActionPickerTestIdSlug,
  type IActionPickerNode,
  type IActionPickerOption,
  type IActionPickerSearchEntry,
  type IActionPickerSearchResult
} from "./action-picker-node";
import { ActionPickerPreview } from "./action-picker-preview";
import "./action-picker-popup.scss";

/** How many search hits are listed — past it the list asks for a narrower query instead. */
export const ACTION_PICKER_SEARCH_LIMIT: number = 99;

/** Highest count a row spells out; past it "999+" costs a fixed four characters. */
const COUNT_CEILING: number = 999;

export interface IActionPickerPopupProps {
  /** The host's option tree, exactly as its builders produce it. Events at the root become tabs. */
  options: IActionPickerOption[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the same `selectedOptions` antd's Cascader passed to `onChange`. */
  onPick: (selectedOptions: CascaderOptionType[]) => void;
  getActionDefinition: (id: string) => IActionDefinitionDto | undefined;
  /** Why adding this action will stop short (e.g. a domain it needs is off); shown before the click. */
  getLockReason?: (definition: IActionDefinitionDto) => string | undefined;
  /** The trigger. Must accept a ref (Radix anchors to it). */
  children: React.ReactElement;
  searchTestId?: string;
  placeholder?: string;
  className?: string;
  /** The host project's type; the preview lists parameters as the actions panel does on it. */
  projectType?: EProjectType;
}

function formatCount(count: number): string {
  return count > COUNT_CEILING ? `${COUNT_CEILING}+` : String(count);
}

/** "onRowDoubleClick" → "On Row Double Click". Names that are already words are left alone. */
export function toEventDisplayLabel(label: string): string {
  if (!/^on[A-Z]/.test(label)) {
    return label;
  }
  const spaced: string = label.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface IRowProps {
  node: IActionPickerNode;
  isOpen: boolean;
  isCursor: boolean;
  countOverride?: number;
  isDimmed?: boolean;
  onPointerRest: (node: IActionPickerNode) => void;
  onPointerLeave: () => void;
  onActivate: (node: IActionPickerNode) => void;
}

const PickerRow = ({
  node,
  isOpen,
  isCursor,
  countOverride,
  isDimmed,
  onPointerRest,
  onPointerLeave,
  onActivate
}: IRowProps): JSX.Element => {
  const isBranch: boolean = isBranchNode(node);
  const count: number = countOverride ?? node.leafCount;
  const isEmptyFolder: boolean = node.kind === EActionPickerNodeKind.emptyFolder;
  return (
    <div
      role="option"
      aria-selected={isOpen || isCursor}
      aria-disabled={isEmptyFolder || isDimmed || undefined}
      data-testid={`actionPickerRow-${toActionPickerTestIdSlug(node.label)}`}
      data-cursor={isCursor || undefined}
      className={classNames("kuika-action-picker-popup__row", {
        "kuika-action-picker-popup__row--open": isOpen,
        "kuika-action-picker-popup__row--cursor": isCursor,
        "kuika-action-picker-popup__row--custom": node.isCustom,
        "kuika-action-picker-popup__row--dimmed": isDimmed || isEmptyFolder
      })}
      onMouseDown={keepPickerFieldFocus}
      onMouseEnter={(): void => onPointerRest(node)}
      onMouseLeave={onPointerLeave}
      onClick={(): void => onActivate(node)}
    >
      {isBranch && (
        <KuikaSvg
          className="kuika-action-picker-popup__chevron"
          type="arrows"
          name="chevron-left"
          width={14}
          height={14}
        />
      )}
      {node.iconName && (
        <KuikaSvg className="kuika-action-picker-popup__row-icon" name={node.iconName} width={16} height={16} />
      )}
      {isEmptyFolder && (
        <KuikaSvg
          className="kuika-action-picker-popup__row-icon"
          type="sidebar-icons"
          name="folder"
          width={16}
          height={16}
        />
      )}
      <MiddleTruncate className="kuika-action-picker-popup__row-label" text={node.label} />
      {node.isBeta && <KuikaBadge content="BETA" color="purple" />}
      {isBranch && count > 0 && <span className="kuika-action-picker-popup__count">{formatCount(count)}</span>}
      {isEmptyFolder && <span className="kuika-action-picker-popup__count">Empty</span>}
    </div>
  );
};

interface IResultRowProps {
  node: IActionPickerNode;
  query: string;
  isCursor: boolean;
  onPointerRest: (node: IActionPickerNode) => void;
  onActivate: (node: IActionPickerNode) => void;
}

const ResultRow = ({ node, query, isCursor, onPointerRest, onActivate }: IResultRowProps): JSX.Element => {
  const trail: string = getNodeChain(node)
    .slice(0, -1)
    .filter((link: IActionPickerNode): boolean => link.kind !== EActionPickerNodeKind.event)
    .map((link: IActionPickerNode): string => link.label)
    .join(" › ");
  return (
    <div
      role="option"
      aria-selected={isCursor}
      data-cursor={isCursor || undefined}
      data-testid={`actionSearchResult-${toActionPickerTestIdSlug(node.label)}`}
      className={classNames("kuika-action-picker-popup__result", {
        "kuika-action-picker-popup__row--cursor": isCursor,
        "kuika-action-picker-popup__row--custom": node.isCustom
      })}
      onMouseDown={keepPickerFieldFocus}
      onMouseEnter={(): void => onPointerRest(node)}
      onClick={(): void => onActivate(node)}
    >
      <span className="kuika-action-picker-popup__result-main">
        <span className="kuika-action-picker-popup__row-label" title={node.label}>
          {highlightCascaderKeyword(node.label, query, "kuika-action-picker-popup")}
        </span>
      </span>
      {trail && <span className="kuika-action-picker-popup__result-trail">{trail}</span>}
    </div>
  );
};

type IColumn = IPickerColumn<IActionPickerNode>;

const getNodeChildren = (node: IActionPickerNode): IActionPickerNode[] => node.children;
const getNodeTitle = (node: IActionPickerNode): string => node.label;

/**
 * The action picker's popup for every host — the properties panel's "+", the nested If / Else,
 * For Each, Recurring and MQTT lists, and the workflow's dynamic assignment.
 *
 * Columns open RIGHT-TO-LEFT from the "+" (the popup is anchored by its right edge and grows into the
 * canvas), the event level is a segmented control rather than a column, and a preview pane at the
 * open end describes the action under the pointer. The popup widens as columns open and only scrolls
 * sideways once it reaches the room the window has; then it scrolls to the newest column.
 *
 * It only chooses. What gets added is decided by the host's own handler, which receives the same
 * `selectedOptions` the Cascader produced.
 */
export const ActionPickerPopup = ({
  options,
  open,
  onOpenChange,
  onPick,
  getActionDefinition,
  getLockReason,
  projectType,
  children,
  searchTestId = "actionPickerPopupSearch",
  placeholder = "Search action",
  className
}: IActionPickerPopupProps): JSX.Element => {
  const nodes: IActionPickerNode[] = useMemo((): IActionPickerNode[] => toActionPickerNodes(options), [options]);
  const events: IActionPickerNode[] = useMemo(
    (): IActionPickerNode[] =>
      nodes.filter((node: IActionPickerNode): boolean => node.kind === EActionPickerNodeKind.event),
    [nodes]
  );
  const leafEvents: IActionPickerNode[] = useMemo(
    (): IActionPickerNode[] =>
      nodes.filter((node: IActionPickerNode): boolean => node.kind === EActionPickerNodeKind.leafEvent),
    [nodes]
  );

  const [activeEventKey, setActiveEventKey] = useState<string | null>(null);
  const [query, setQuery] = useState<string>("");
  const [categoryFilterKey, setCategoryFilterKey] = useState<string | null>(null);

  const searchRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null> = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);

  const activeEvent: IActionPickerNode | null =
    events.find((event: IActionPickerNode): boolean => event.key === activeEventKey) ?? events[0] ?? null;
  const rootItems: IActionPickerNode[] = useMemo((): IActionPickerNode[] => {
    if (activeEvent) {
      return activeEvent.children;
    }
    return nodes.filter((node: IActionPickerNode): boolean => node.kind !== EActionPickerNodeKind.leafEvent);
  }, [activeEvent, nodes]);

  const nodesByKey: Map<string, IActionPickerNode> = useMemo((): Map<string, IActionPickerNode> => {
    const map: Map<string, IActionPickerNode> = new Map<string, IActionPickerNode>();
    const walk = (list: IActionPickerNode[]): void => {
      for (const node of list) {
        map.set(node.key, node);
        walk(node.children);
      }
    };
    walk(nodes);
    return map;
  }, [nodes]);

  const pick = useCallback(
    (node: IActionPickerNode): void => {
      if (!isAddableNode(node)) {
        return;
      }
      onOpenChange(false);
      onPick(getSelectedOptionsPath(node));
    },
    [onOpenChange, onPick]
  );

  const engine: IPickerColumnsController<IActionPickerNode> = usePickerColumns<IActionPickerNode>({
    rootItems,
    rootTitle: "Category",
    getChildren: getNodeChildren,
    getTitle: getNodeTitle,
    isBranch: isBranchNode,
    isPickable: isAddableNode,
    onPick: pick
  });
  const {
    columns,
    openKeys,
    setOpenKeys,
    previewKey,
    setPreviewKey,
    cursor,
    setCursor,
    stripRef,
    handleRowRest,
    handleRowLeave,
    handleRowActivate,
    handleStripWheel
  } = engine;

  const searchIndex: IActionPickerSearchEntry[] = useMemo(
    (): IActionPickerSearchEntry[] => buildSearchIndex(rootItems),
    [rootItems]
  );
  const isSearching: boolean = query.trim() !== "";
  const searchResult: IActionPickerSearchResult | null = useMemo(
    (): IActionPickerSearchResult | null => (isSearching ? searchActionPicker(searchIndex, query) : null),
    [isSearching, searchIndex, query]
  );
  const filteredMatches: IActionPickerNode[] = useMemo((): IActionPickerNode[] => {
    if (!searchResult) {
      return [];
    }
    if (!categoryFilterKey) {
      return searchResult.matches;
    }
    return searchResult.matches.filter(
      (node: IActionPickerNode): boolean => getCategoryAncestor(node)?.key === categoryFilterKey
    );
  }, [searchResult, categoryFilterKey]);
  const shownMatches: IActionPickerNode[] = filteredMatches.slice(0, ACTION_PICKER_SEARCH_LIMIT);

  const previewNode: IActionPickerNode | null = previewKey ? (nodesByKey.get(previewKey) ?? null) : null;
  const previewDefinition: IActionDefinitionDto | undefined =
    previewNode && previewNode.kind === EActionPickerNodeKind.action
      ? getActionDefinition(String(previewNode.option.value))
      : undefined;
  const lastColumn: IColumn = columns[columns.length - 1];
  const hasActionColumn: boolean = lastColumn.items.some((node: IActionPickerNode): boolean => isAddableNode(node));
  const isPreviewShown: boolean = isSearching ? shownMatches.length > 0 : hasActionColumn;

  // Reset everything but the chosen event whenever the popup opens, and whenever the tree changes
  // under an open popup (the host rebuilds its options on open).
  useEffect((): void => {
    if (open) {
      setOpenKeys([]);
      setPreviewKey(null);
      setQuery("");
      setCategoryFilterKey(null);
      setCursor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nodes]);

  useEffect((): void => {
    setCategoryFilterKey(null);
    setCursor(isSearching ? { column: 0, row: 0 } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isSearching]);

  // The search list's first row is what Enter adds; keep the preview on it until the pointer moves.
  useEffect((): void => {
    if (isSearching) {
      setPreviewKey(shownMatches[0]?.key ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult, categoryFilterKey]);

  useRevealNewestColumn(stripRef, openKeys, isPreviewShown);
  useCloseOnIframeFocus(open, onOpenChange);

  const handleResultRest = useCallback(
    (node: IActionPickerNode): void => engine.handleResultRest(node, shownMatches.indexOf(node)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shownMatches]
  );

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (isSearching) {
      engine.handleResultsKeyDown(event, shownMatches);
      return;
    }
    engine.handleBrowseKeyDown(event);
  };

  const handleEscape = (event: KeyboardEvent): void => {
    if (query !== "") {
      event.preventDefault();
      setQuery("");
    }
  };

  // Every host with an event level shows it, even a lone one (a screen's page init, an API method's
  // actions), so the picker always says where the action lands. A screen's page init is named as the
  // actions panel names the trigger.
  // The tab and the preview's "Add to …" button read the same name (KOA-18648 QA: "Page Init" vs "Initial Actions").
  const isScreenRoot: boolean = events.length === 1 && leafEvents.length > 0;
  const getEventName = (event: IActionPickerNode): string =>
    isScreenRoot && event.option.value === "pageInit" ? "Page Init" : toEventDisplayLabel(event.label);
  const eventTabs: KuikaTabOption[] = events.map((event: IActionPickerNode): KuikaTabOption => ({
    value: event.key,
    label: getEventName(event),
    dataTestId: `actionPickerEventTab-${toActionPickerTestIdSlug(event.label)}`
  }));
  const hasEventTabs: boolean = events.length > 0;
  const leafEventButtons: JSX.Element[] = leafEvents.map((leafEvent: IActionPickerNode): JSX.Element => (
    <KuikaTooltip key={leafEvent.key} content="Adds now: a block whose actions repeat on an interval.">
      <KuikaButton
        type="tertiary"
        size="sm"
        className="kuika-action-picker-popup__leaf-event"
        data-testid="actionPickerRecurringButton"
        onMouseDown={keepPickerFieldFocus}
        onClick={(): void => pick(leafEvent)}
      >
        <KuikaSvg name="repeat" width={14} height={14} />
        {leafEvent.label}
      </KuikaButton>
    </KuikaTooltip>
  ));
  const eventLabel: string | undefined = activeEvent ? getEventName(activeEvent) : undefined;
  const hasAnything: boolean = rootItems.length > 0;

  const categoryCounts: Map<string, number> | null = searchResult?.countsByCategory ?? null;
  const resultCountLabel: string | null = searchResult
    ? `${Math.min(filteredMatches.length, ACTION_PICKER_SEARCH_LIMIT)}${filteredMatches.length > ACTION_PICKER_SEARCH_LIMIT ? "+" : ""} results`
    : null;

  const renderBrowse = (): JSX.Element => (
    <>
      {columns.map((column: IColumn, columnIndex: number): JSX.Element => (
        <div
          key={column.owner?.key ?? "root"}
          className={classNames("kuika-action-picker-popup__column", {
            "kuika-action-picker-popup__column--root": columnIndex === 0,
            "kuika-action-picker-popup__column--actions": column.items.some(isAddableNode)
          })}
          role="listbox"
          aria-label={column.title}
          data-testid={`actionPickerColumn-${columnIndex}`}
        >
          <div className="kuika-action-picker-popup__column-title" title={column.title}>
            {column.title}
          </div>
          <div className="kuika-action-picker-popup__column-rows">
            {column.items.map((node: IActionPickerNode, rowIndex: number): JSX.Element => (
              <PickerRow
                key={node.key}
                node={node}
                isOpen={openKeys[columnIndex] === node.key || previewKey === node.key}
                isCursor={cursor?.column === columnIndex && cursor.row === rowIndex}
                onPointerRest={handleRowRest}
                onPointerLeave={(): void => handleRowLeave(columnIndex)}
                onActivate={handleRowActivate}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );

  const renderSearch = (): JSX.Element => (
    <>
      <div
        className="kuika-action-picker-popup__column kuika-action-picker-popup__column--root"
        role="listbox"
        aria-label="Categories"
        data-testid="actionPickerColumn-0"
      >
        <div className="kuika-action-picker-popup__column-title">Category</div>
        <div className="kuika-action-picker-popup__column-rows">
          <div
            role="option"
            aria-selected={categoryFilterKey === null}
            className={classNames("kuika-action-picker-popup__row", {
              "kuika-action-picker-popup__row--open": categoryFilterKey === null
            })}
            onMouseDown={keepPickerFieldFocus}
            onClick={(): void => setCategoryFilterKey(null)}
          >
            <KuikaSvg className="kuika-action-picker-popup__row-icon" name="search" width={16} height={16} />
            <span className="kuika-action-picker-popup__row-label">All results</span>
            <span className="kuika-action-picker-popup__count">{formatCount(searchResult?.matches.length ?? 0)}</span>
          </div>
          {rootItems.map((node: IActionPickerNode): JSX.Element => {
            const hits: number = categoryCounts?.get(node.key) ?? 0;
            return (
              <PickerRow
                key={node.key}
                node={node}
                isOpen={categoryFilterKey === node.key}
                isCursor={false}
                countOverride={hits}
                isDimmed={hits === 0}
                onPointerRest={(): void => undefined}
                onPointerLeave={(): void => undefined}
                onActivate={(target: IActionPickerNode): void => {
                  if (hits > 0) {
                    setCategoryFilterKey(target.key);
                  }
                }}
              />
            );
          })}
        </div>
      </div>
      <div
        className="kuika-action-picker-popup__column kuika-action-picker-popup__column--results"
        role="listbox"
        aria-label="Search results"
        data-testid="actionPickerSearchResults"
      >
        <div className="kuika-action-picker-popup__column-title">{resultCountLabel}</div>
        <div className="kuika-action-picker-popup__column-rows">
          {shownMatches.map((node: IActionPickerNode, rowIndex: number): JSX.Element => (
            <ResultRow
              key={node.key}
              node={node}
              query={query}
              isCursor={cursor?.row === rowIndex}
              onPointerRest={handleResultRest}
              onActivate={pick}
            />
          ))}
          {filteredMatches.length > ACTION_PICKER_SEARCH_LIMIT && (
            <div className="kuika-action-picker-popup__more">
              Showing {ACTION_PICKER_SEARCH_LIMIT} of {filteredMatches.length}. Add another word to narrow the list.
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderBody = (): JSX.Element => {
    if (!hasAnything) {
      return (
        <div className="kuika-action-picker-popup__empty">
          <KuikaEmptyState
            variant="embedded"
            svgName="lock"
            customName="No actions to add here"
            description="Adding actions needs the Add/Edit System Actions or Manage Custom Actions right. Ask a workspace admin."
          />
        </div>
      );
    }
    if (isSearching && shownMatches.length === 0 && (searchResult?.matches.length ?? 0) === 0) {
      return (
        <div className="kuika-action-picker-popup__empty">
          <KuikaEmptyState
            variant="embedded"
            svgName="search-off"
            customName={`No actions match "${query.trim()}"`}
            description="Check the spelling, or search for a category such as Arithmetic."
            footer={
              <KuikaButton
                type="secondary"
                size="sm"
                onMouseDown={keepPickerFieldFocus}
                onClick={(): void => setQuery("")}
              >
                Clear search
              </KuikaButton>
            }
          />
        </div>
      );
    }
    return (
      <div ref={stripRef} className="kuika-action-picker-popup__strip" onWheel={handleStripWheel}>
        {isSearching ? renderSearch() : renderBrowse()}
        {isPreviewShown && (
          <ActionPickerPreview
            node={previewNode}
            definition={previewDefinition}
            eventLabel={eventLabel}
            lockReason={previewDefinition && getLockReason ? getLockReason(previewDefinition) : undefined}
            projectType={projectType}
            onAdd={pick}
          />
        )}
      </div>
    );
  };

  const breadcrumb: IActionPickerNode[] = columns
    .map((column: IColumn): IActionPickerNode | null => column.owner)
    .filter((owner: IActionPickerNode | null): owner is IActionPickerNode => owner !== null);

  const content: JSX.Element = (
    <div className="kuika-action-picker-popup__surface" data-testid="actionPickerPopup">
      <div className="kuika-action-picker-popup__header">
        <div className="kuika-action-picker-popup__search-row">
          <KuikaInput
            ref={searchRef}
            className="kuika-action-picker-popup__search"
            size="md"
            value={query}
            placeholder={placeholder}
            data-testid={searchTestId}
            aria-label={placeholder}
            leftIcon={<KuikaSvg name="search" width={16} height={16} />}
            rightUnit={
              resultCountLabel ? (
                <span
                  className="kuika-action-picker-popup__result-count"
                  data-testid="actionPickerPopupSearchResultCount"
                >
                  {resultCountLabel}
                </span>
              ) : undefined
            }
            allowClear
            onChange={(_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
              setQuery(String(value))
            }
            onKeyDown={handleSearchKeyDown}
          />
          {hasEventTabs && (
            <div className="kuika-action-picker-popup__event-tabs">
              <KuikaTabs
                options={eventTabs}
                value={activeEvent?.key}
                ariaLabel="Event"
                dataTestId="actionPickerEventTabs"
                onChange={(value: string): void => {
                  setActiveEventKey(value);
                  setOpenKeys([]);
                  setPreviewKey(null);
                  setCursor(null);
                  setCategoryFilterKey(null);
                  // The segment is a radio group and takes focus on click; typing goes back to search.
                  requestAnimationFrame((): void => searchRef.current?.focus());
                }}
              />
            </div>
          )}
          {leafEventButtons}
        </div>
      </div>
      {renderBody()}
      <div className="kuika-action-picker-popup__footer">
        <span className="kuika-action-picker-popup__crumbs">
          {breadcrumb.length > 0 && !isSearching
            ? breadcrumb.map((owner: IActionPickerNode, index: number): JSX.Element => (
                <React.Fragment key={owner.key}>
                  {index > 0 && <span className="kuika-action-picker-popup__crumb-separator">›</span>}
                  <span>{owner.label}</span>
                </React.Fragment>
              ))
            : null}
        </span>
        <span className="kuika-action-picker-popup__keys">
          <span className="kuika-action-picker-popup__kbd">↑↓</span> move
          {!isSearching && (
            <>
              <span className="kuika-action-picker-popup__kbd">←</span> open
              <span className="kuika-action-picker-popup__kbd">→</span> back
            </>
          )}
          <span className="kuika-action-picker-popup__kbd">↵</span> add
        </span>
      </div>
    </div>
  );

  return (
    <KuikaPopover
      visible={open}
      onVisibleChange={onOpenChange}
      content={content}
      // Left of the "+", top edge level with it: the popup grows into the canvas instead of hanging
      // under the trigger, so its height is never cut down to the room below a low "+" (it opened
      // ~280px tall there); Radix shifts it up when the full body does not fit under the trigger's top.
      position="left"
      align="start"
      sideOffset={PICKER_SIDE_OFFSET}
      collisionPadding={PICKER_COLLISION_PADDING}
      className={classNames("kuika-action-picker-popup", className)}
      onEscapeKeyDown={handleEscape}
      onKeyDown={stopPickerKeyPropagation}
      onMouseDownCapture={keepPickerFocusInField}
      onOpenAutoFocus={(event: Event): void => {
        // Straight into the search field — Radix's default is the first tabbable element, which can
        // be a tab or the Recurring button, and focusing that button pops its tooltip open.
        event.preventDefault();
        searchRef.current?.focus();
      }}
    >
      {children}
    </KuikaPopover>
  );
};

export default ActionPickerPopup;
